import { Suspense, useCallback, useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { PRODUCT_RSIM_TABLE_CONFIGURATION } from '../Components/models/configurations';
import { KickRsimProvider } from '../Components/KickRsimProvider';
import { Routes } from 'src/Routes.constants';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Actions, Subjects } from 'permissions/ability';
import { ConfigurableTable } from 'common/components/ConfigurableTable/ConfigurableTable';
import { TextCell } from 'common/components/ConfigurableTable/Components/Cells/TextCell';
import { RSimSimTable } from '../Components/SimInventory/RSimSimTable';
import { DetailsPanel } from 'common/components/ConfigurableTable/Components/Containers/DetailsPanel';
import { RSimStatusCell } from '../Components/Cells/RSimStatusCell';
import { RSimUpdateProvider } from '../Components/Providers/RSimUpdateProvider';
import { RSimTagsCell } from '../Components/Cells/RSimTagsCell';
import { RSimConfigurationDialog } from '../Components/Dialogs/RSimConfigurationDialog';
import { DurationCell } from 'common/components/ConfigurableTable/Components/Cells/DurationCell';
import { RSimsSimsProvider } from '../Components/Providers/RSimsSimsProvider';
import { OtaCell } from '../Components/Cells/OtaCell';
import { RSimEIDLinkCell } from '../Components/Cells/RSimEIDLinkCell';
import { useAtom } from 'jotai';
import {
  pageFilterAtom,
  rowsPerPageFilterAtom,
  rsimSortingAtomWithStorage,
} from 'atoms/filters/rsim-products';
import { GridPaginationModel, GridRowParams, GridSortModel } from '@mui/x-data-grid-pro';
import { ContentBox } from 'common/components/AppLayout/ContentBox';
import { TableSkeleton } from 'common/components/Table/TableSkeleton';
import {
  TableLayoutActions,
  TableLayoutTable,
} from 'common/components/Layouts/TableLayout/TableLayout';
import { RefetchQueryButton } from 'common/components/Queries/RefetchQueryButton';
import { useHasFeatureFlag } from 'featureFlags/useHasFeatureFlag';
import { ActivationDialog } from 'rSimInventory/Inventory/Actions/ActivationDialog';
import { Action } from 'common/components/ConfigurableTable/models/configurableTable';
import { DeactivationDialog } from 'rSimInventory/Inventory/Actions/DeactivationDialog';
import { SubscriptionStatus } from 'src/common/components/SubscriptionStatus/constants/subscriptionStatus';
import { useIsFetching } from '@tanstack/react-query';
import { RSimDownloadLargeReportButton } from '../reports/RSimDownloadLargeReportButton';
import { SimInventoryCan } from 'permissions/PermissionProvider.tsx';
import { useRSimProducts } from '../hooks/useRSimProducts.ts';
import { useRSimProductsPagination } from '../hooks/useRSimProductsPagination';
import { rSimProductsQueries } from '../utils/rsimProductsQueries';
import { RSimProductsInventoryEditDialog } from './EditDialog/RSimProductsInventoryEditDialog';
import { ACTIVATION_ACTIONS } from 'src/common/components/Products/actions/constants/activationActionsConstants';
import { SubscriptionStatusCell } from 'src/common/components/ConfigurableTable/Components/Cells/SubscriptionStatusCell';
import { NumberCell } from 'src/common/components/ConfigurableTable/Components/Cells/NumberCell';
import { ColumnHeaders } from 'src/common/components/ConfigurableTable/Components/Rows/ColumnHeaders';
import { GridRow, RowRoot } from 'src/common/components/ConfigurableTable/Components/Rows/GridRow';
import { useRsimLastPollingTimestamps } from '../hooks/useRsimLastPollingTimestamp';
import { LastPollingTimeStampCellCell } from 'src/common/components/ConfigurableTable/Components/Cells/LastPollingTimestampCell';
import { RSimInventoryItem } from '../constants/rsim';
import { getFontScale } from 'src/common/utils/fonts';
import { DEFAULT_BORDER_RADIUS } from 'src/theme/constants';

import Box from '@mui/material/Box';
import { useRSimSelectionModel } from 'rSimInventory/Inventory/hooks/useRSimSelectionModel.ts';
import { useIsFetchingQueryTable } from 'rSimInventory/Inventory/hooks/useIsFetchingQueryTable.ts';
import { useIsRSimInLifeChangesAvailable } from 'rSimInventory/Actions/hooks/useIsRSimInLifeChangesAvailable.ts';

const CustumizedConfigurationTable = styled(ConfigurableTable)(({ theme }) => ({
  '& .MuiDataGrid-overlayWrapper': {
    borderLeft: `1px solid ${theme.palette.border} !important`,
    borderRight: `1px solid ${theme.palette.border} !important`,
  },
  '& .MuiDataGrid-cell': {
    borderTop: 'none',
  },
  '& .MuiDataGrid-cell--pinnedRight': {
    right: '0 !important',
  },
  '& .MuiDataGrid-columnHeader[data-field="rSimStatus"] .MuiTypography-text3': {
    textTransform: 'none',
  },
  '& .MuiDataGrid-footerContainer': {
    border: `1px solid ${theme.palette.border}`,
    borderTop: 'none',
    overflow: 'hidden',
    borderBottomLeftRadius: DEFAULT_BORDER_RADIUS,
    borderBottomRightRadius: DEFAULT_BORDER_RADIUS,
  },
  '& .MuiDataGrid-filler': {
    visibility: 'hidden',
  },

  [`.MuiDataGrid-detailPanel ~ ${RowRoot}`]: {
    borderTop: `1px solid ${theme.palette.border} !important`,
  },
}));

const RSIM_SLOTS = {
  columnHeaders: ColumnHeaders,
  row: GridRow,
};

const RSIM_CELL_CONFIGURATION = {
  default: ({
    value,
    subValue,
  }: {
    field: string;
    value?: string;
    subValue?: string;
    colDef: any;
  }) => {
    return <TextCell value={value} subValue={subValue} />;
  },
  euicc: RSimEIDLinkCell,
  rSimStatus: RSimStatusCell,
  tags: RSimTagsCell,
  appliedOtaConfigurationProfile: OtaCell,
  outMinuteUsage: DurationCell,
  dataUsage: NumberCell,
  outTextUsage: ({
    value,
    subValue,
    colDef,
  }: {
    value?: string;
    subValue?: string;
    colDef: any;
  }) => {
    return <NumberCell value={value ?? ''} subValue={colDef.subField ? subValue : ''} />;
  },
  subscriptionStatus: ({ value }: { value: SubscriptionStatus }) => {
    return <SubscriptionStatusCell subscriptionStatus={value} />;
  },
  lastPollingTimestamp: ({ value }: { value: string }) => {
    return <LastPollingTimeStampCellCell value={value} />;
  },
};

const getDetailPanelContent = (props: GridRowParams) => {
  return (
    <DetailsPanel>
      <RSimSimTable
        euicc={props.row.euicc}
        primaryIccid={props.row.primaryIccid}
        secondaryIccid={props.row.secondaryIccid}
        status={props.row.status}
      />
    </DetailsPanel>
  );
};
const getDetailPanelHeight = () => 176 * getFontScale();

type RSimTableContentProps = {
  totalItems: number;
};

export const RSimTableContent = ({ totalItems }: RSimTableContentProps) => {
  const { rowSelectionModel, onRowSelectionModelChange, unSelectAll } = useRSimSelectionModel();
  const [rowsPerPage, setRowsPerPage] = useAtom(rowsPerPageFilterAtom);
  const [page, setPage] = useAtom(pageFilterAtom);
  const onPaginationChange = useCallback(
    (pagination: GridPaginationModel) => {
      if (pagination.page !== page) {
        unSelectAll();
        setPage(pagination.page);
      }

      if (pagination.pageSize !== rowsPerPage) {
        setRowsPerPage(pagination.pageSize);
      }
    },
    [page, rowsPerPage, setPage, setRowsPerPage, unSelectAll],
  );

  const [sorting, setSorting] = useAtom(rsimSortingAtomWithStorage);

  const rsimConnectivityMonitoringEnabled = useHasFeatureFlag('RsimConnectivityMonitoringEnabled');
  const productsViewRsimActivationEnabled = useHasFeatureFlag('ProductsViewRsimActivation');

  const {
    queryResult: { data },
    queryKey,
  } = useRSimProducts();

  const euiccs = useMemo(() => data?.items.map((item) => item.euicc), [data]) || [];

  useEffect(() => {
    unSelectAll();
  }, [data?.items]);

  const { data: timestamps } = useRsimLastPollingTimestamps({
    euiccs,
  });

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const onSortModelChange = useCallback((model: GridSortModel) => {
    const [sortField] = model;
    if (!sortField) {
      return;
    }
    //temporary solution
    if (sortField.field === 'euicc') {
      setSorting({
        columnName: 'EID',
        sort: sortField.sort ?? null,
      });
    } else {
      setSorting({
        columnName: sortField.field,
        sort: sortField.sort ?? null,
      });
    }
  }, []);

  const actions = useMemo(() => {
    const menuActions: Action[] = [
      {
        id: 'viewDetails',
        label: 'View Details',
        type: 'custom',
        actionCallback: (data: any) => {
          navigate(`${Routes.productsRsim}/${data.euicc}`, {
            state: {
              previousPath: `${pathname}${search}`,
            },
          });
        },
      },
      {
        id: 'activation',
        label: 'Activate',
        type: 'dialog',
        Component: ActivationDialog,
        enabled: (data) => {
          return data.activationAction === ACTIVATION_ACTIONS.ACTIVATE && !inlifeChangesEnabled;
        },
        permission: {
          action: Actions.activation,
          subject: Subjects.rSimProducts,
        },
      },
      {
        id: 'deactivation',
        label: 'Deactivate',
        type: 'dialog',
        Component: DeactivationDialog,
        enabled: (data) => {
          return data.activationAction === ACTIVATION_ACTIONS.DEACTIVATE && !inlifeChangesEnabled;
        },
      },
      {
        id: 'edit',
        label: 'Edit',
        type: 'dialog',
        Component: RSimProductsInventoryEditDialog,
        permission: {
          action: Actions.edit,
          subject: Subjects.tags,
        },
      },
      {
        id: 'otaConfiguration',
        label: t('rSimInventory.configurationDetails'),
        type: 'dialog',
        Component: RSimConfigurationDialog,
        permission: {
          action: Actions.read,
          subject: Subjects.otaConfiguration,
        },
      },
    ];

    return menuActions.filter((actions) => {
      if (productsViewRsimActivationEnabled) {
        return true;
      }

      if (actions.type === 'separtor') {
        return true;
      }

      return actions.id !== 'activation' && actions.id !== 'deactivation';
    });
  }, [pathname, search]);

  const mappedSorting = useMemo(() => {
    //temporary solution
    if (sorting?.columnName === 'EID') {
      return {
        ...sorting,
        columnName: 'euicc',
      };
    }

    return sorting;
  }, [sorting]);

  const tableData = useMemo(() => {
    return rsimConnectivityMonitoringEnabled
      ? data?.items.map((rsim: RSimInventoryItem) => {
          return {
            ...rsim,
            lastPollingTimestamp: timestamps ? timestamps[rsim.euicc] : null,
          };
        })
      : data.items;
  }, [data?.items, timestamps]);
  const inlifeChangesEnabled = useIsRSimInLifeChangesAvailable();

  const isFetching = useIsFetchingQueryTable();

  const getRowHeight = useCallback(() => {
    return 52 * getFontScale();
  }, []);

  return (
    <>
      {isFetching && (
        <ContentBox>
          <TableSkeleton showStartSkeleton numberOfRows={20} />
        </ContentBox>
      )}
      <Box
        sx={{
          display: !isFetching ? 'block' : 'none',
          height: !isFetching ? '100%' : '0',
          width: '100%',
        }}
      >
        <KickRsimProvider euiccs={euiccs}>
          <RSimUpdateProvider queryKey={queryKey}>
            <RSimsSimsProvider rsims={data?.items}>
              <CustumizedConfigurationTable
                disableRowSelectionExcludeModel
                checkboxSelection={inlifeChangesEnabled}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={onRowSelectionModelChange}
                getRowHeight={getRowHeight}
                sort={mappedSorting}
                disableColumnReorder
                actions={actions}
                data-testid="rsims inventory table"
                configuration={PRODUCT_RSIM_TABLE_CONFIGURATION}
                getDetailPanelContent={getDetailPanelContent}
                getDetailPanelHeight={getDetailPanelHeight}
                rows={tableData}
                cellConfiguration={RSIM_CELL_CONFIGURATION}
                totalNumberOfItems={totalItems}
                onPaginationChange={onPaginationChange}
                page={page}
                pageSize={rowsPerPage}
                onSortModelChange={onSortModelChange}
                slots={RSIM_SLOTS}
              />
            </RSimsSimsProvider>
          </RSimUpdateProvider>
        </KickRsimProvider>
      </Box>
    </>
  );
};

export const RSimTable = () => {
  const isFetching = useIsFetching({ queryKey: rSimProductsQueries.all });

  const { data: paginationData, isLoading } = useRSimProductsPagination();

  return (
    <>
      <TableLayoutActions showBorder>
        <RefetchQueryButton queryKey={rSimProductsQueries.all} />
        <SimInventoryCan I={Actions.export} a={Subjects.rSimProducts}>
          <RSimDownloadLargeReportButton
            totalItems={paginationData}
            disabled={isFetching > 0 || isLoading || paginationData <= 0}
          />
        </SimInventoryCan>
      </TableLayoutActions>

      <TableLayoutTable>
        <Suspense
          fallback={
            <ContentBox>
              <TableSkeleton showStartSkeleton numberOfRows={20} />
            </ContentBox>
          }
        >
          <RSimTableContent totalItems={paginationData} />
        </Suspense>
      </TableLayoutTable>
    </>
  );
};
